// src/pages/StateAdminReport/StateAdminReport.jsx
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faHandshake,
  faTentArrowLeftRight,
  faUsers,
  faCalendarDay
} from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the State Admin Report options
const stateAdminReportOptions = [
  {
    title: 'All STATE HEAD',
    icon: faUsers,
    path: '/state-admin-report/all-state-heads',
    perm: 'stateHead.all',
    description: 'View and export complete list of State Heads'
  },
  {
    title: 'STATE HEAD Daily Report',
    icon: faCalendarDay,
    path: '/state-admin-report/daily-report',
    perm: 'stateHead.daily',
    description: 'View daily transaction volume and commission reports per State Head (Levels 1, 2, 3)'
  },
  {
    title: 'STATE HEAD Transaction Report',
    icon: faExchangeAlt,
    path: '/state-admin-report/transactions',
    perm: 'stateHead.transactions',
    description: 'View detailed transaction history from all shops under State Admin supervision'
  },
  {
    title: 'STATE HEAD Settlements Handle',
    icon: faTentArrowLeftRight,
    path: '/state-admin-report/commission-transactions',
    perm: 'stateHead.settlementsHandle',
    description: 'Settle State Head commissions and view transaction history'
  },
  {
    title: 'STATE HEAD Settlements Report',
    icon: faHandshake,
    path: '/state-admin-report/settlements-report',
    perm: 'stateHead.settlementsReport',
    description: 'View settlement and withdrawal transaction history for all State Heads'
  }
];

const StateAdminReport = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  // Level decides navigation scope: L2 goes straight to own reports, L1 to "all" lists
  const { userLevel, userId } = useMemo(() => {
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = token ? jwtDecode(token) : {};
      return { userLevel: decodedToken.level ?? null, userId: decodedToken.userId || '' };
    } catch (error) {
      return { userLevel: null, userId: '' };
    }
  }, []);

  const handleCardClick = useCallback((path) => {
    // For Level 2 users (State Admin), go directly to their reports using their userId
    if (userLevel === 2) {
      if (path.includes('commission-transactions')) {
        navigate(`/state-admin-report/commission-transactions/state-admin-commission-transactions/${userId}`);
      } else if (path.includes('transactions') && !path.includes('commission-transactions')) {
        navigate(`/state-admin-report/transactions/state-admin-transactions/${userId}`);
      } else if (path.includes('commissions') && !path.includes('commission-transactions')) {
        navigate(`/state-admin-report/commissions/state-admin-commissions/${userId}`);
      } else {
        navigate(path);
      }
    } else if (userLevel === 1) {
      // For Level 1 users (Super Admin), show State Admin selection pages
      if (path.includes('commission-transactions')) {
        navigate('/state-admin-report/commission-transactions/all-state-admin-commission-transactions');
      } else if (path.includes('transactions') && !path.includes('commission-transactions')) {
        navigate('/state-admin-report/transactions/all-state-admin-transactions');
      } else if (path.includes('commissions') && !path.includes('commission-transactions')) {
        navigate('/state-admin-report/commissions/all-state-admin-commissions');
      } else {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  }, [navigate, userLevel, userId]);

  const getUserLevelText = (level) => {
    switch(level) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      default: return `Level ${level}`;
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = stateAdminReportOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access State Admin reports. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      
      {/* Header with user level info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: -7 }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', flex: 1, fontWeight: 'bold', color: 'primary.main' }}>
          STATE HEAD MODULE
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getUserLevelText(userLevel)}
        </Typography>
      </Box>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {stateAdminReportOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 280,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'warning.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      style={{ color: 'orange', marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
      
      {/* Debug Info for development
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Debug Info:</strong> User Level: {userLevel} | User ID: {userId} | 
          </Typography>
        </Box>
      )} */}
    </Box>
  );
};

export default StateAdminReport;